import type { ValidatedBirthInput } from './birthInput.js';
import type { IztroAstrolabeSnapshot, IztroHoroscopeSnapshot, IztroPortCalculationConfig, IztroRuntimePort } from './iztroAdapter.js';

interface IztroHoroscopeLike { toJSON(): IztroHoroscopeSnapshot; }
interface IztroFunctionalPalaceLike { name:string; heavenlyStem:string; mutagedPlaces(): readonly ({name:string}|undefined)[]; }
interface IztroAstrolabeLike { toJSON(): IztroAstrolabeSnapshot; horoscope(date?: string | Date, timeIndex?: number): IztroHoroscopeLike; palace(indexOrName:number|string):IztroFunctionalPalaceLike|undefined; palaces?:readonly IztroFunctionalPalaceLike[]; }
interface IztroUpstreamConfigSnapshot {
  mutagens?: Record<string, readonly string[]>;
  brightness?: Record<string, readonly string[]>;
  yearDivide: 'normal' | 'exact';
  horoscopeDivide: 'normal' | 'exact';
  ageDivide: 'normal' | 'birthday';
  dayDivide: 'forward' | 'current';
  algorithm: 'default' | 'zhongzhou';
}
export interface IztroModuleLike {
  util: { getMutagensByHeavenlyStem(stem: string): string[]; };
  astro: {
    config(config: Record<string, unknown>): void;
    getConfig(): IztroUpstreamConfigSnapshot;
    bySolar(date: string, timeIndex: number, gender: string, fixLeap: boolean, language: 'zh-TW'): IztroAstrolabeLike;
    byLunar(date: string, timeIndex: number, gender: string, isLeapMonth: boolean, fixLeap: boolean, language: 'zh-TW'): IztroAstrolabeLike;
  };
}

function hasOwnEntries(value: Record<string, readonly string[]> | undefined): boolean {
  return Boolean(value && Object.keys(value).length > 0);
}

const UPSTREAM_STAR_LABEL_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  '廉贞': '廉貞',
  '破军': '破軍',
  '太阳': '太陽',
  '天机': '天機',
  '太阴': '太陰',
  '巨门': '巨門',
  '贪狼': '貪狼',
  '左辅': '左輔',
});

function normalizeUpstreamStarLabel(label: string): string {
  const trimmed = label.trim();
  return UPSTREAM_STAR_LABEL_ALIASES[trimmed] ?? trimmed;
}

function upstreamMutagens(config: IztroPortCalculationConfig): Record<string, readonly string[]> {
  return Object.fromEntries(
    Object.entries(config.mutagens).map(([stem, row]) => [stem, [row.lu, row.quan, row.ke, row.ji] as const]),
  );
}

function snapshotWithPalaceTransformations(module:IztroModuleLike,astrolabe:IztroAstrolabeLike):IztroAstrolabeSnapshot{
  const raw=astrolabe.toJSON();
  if(raw.palaceTransformations?.length===12)return raw;
  if(!astrolabe.palaces?.length)throw new Error('Iztro functional astrolabe does not expose palace transformation methods');
  const labels=['祿','權','科','忌'] as const;
  const palaceTransformations=raw.palaces.map((rawPalace,index)=>{
    // iztro 2.6.0 only binds the parent astrolabe when a functional palace is
    // resolved through astrolabe.palace(...). Iterating astrolabe.palaces
    // directly can leave mutagedPlaces() detached and silently return [].
    const palace=astrolabe.palace(index);
    if(!palace||palace.name!==rawPalace.name)throw new Error(`Iztro functional palace lookup failed for ${rawPalace.name}`);
    const stars=module.util.getMutagensByHeavenlyStem(palace.heavenlyStem).map(normalizeUpstreamStarLabel);
    const targets=palace.mutagedPlaces();
    if(stars.length!==4||targets.length!==4||targets.some((target)=>!target))throw new Error(`Iztro palace transformation extraction failed for ${palace.name}`);
    return{sourcePalaceName:palace.name,sourceHeavenlyStem:palace.heavenlyStem,targets:labels.map((mutagen,targetIndex)=>({mutagen,starName:stars[targetIndex]!,targetPalaceName:targets[targetIndex]!.name}))};
  });
  return{...raw,palaceTransformations};
}

export function createIztroRuntimePort(module: IztroModuleLike): IztroRuntimePort {
  let applied: IztroPortCalculationConfig | undefined;
  return {
    engineVersion: '2.6.0',
    assertIsolatedRuntime() {
      const current = module.astro.getConfig();
      if (hasOwnEntries(current.mutagens) || hasOwnEntries(current.brightness)) {
        throw new Error('Iztro runtime is not isolated: custom global mutagens/brightness already exist');
      }
    },
    applyCalculationConfig(config) {
      module.astro.config({
        mutagens: upstreamMutagens(config),
        yearDivide: config.yearDivide,
        horoscopeDivide: config.horoscopeDivide,
        ageDivide: config.ageDivide,
        dayDivide: config.dayDivide,
        algorithm: config.algorithm,
      });
      applied = structuredClone(config);
    },
    readCalculationConfig() {
      if (!applied) throw new Error('Iztro runtime config has not been applied');
      const current = module.astro.getConfig();
      for (const key of ['yearDivide', 'horoscopeDivide', 'ageDivide', 'dayDivide', 'algorithm'] as const) {
        if (current[key] !== applied[key]) throw new Error(`Iztro upstream config mismatch for ${key}`);
      }
      for (const [stem, row] of Object.entries(applied.mutagens)) {
        const expected=[row.lu,row.quan,row.ke,row.ji];
        const actual=module.util.getMutagensByHeavenlyStem(stem).map(normalizeUpstreamStarLabel);
        if (JSON.stringify(actual)!==JSON.stringify(expected)) throw new Error(`Iztro upstream mutagen mismatch for ${stem}`);
      }
      return structuredClone(applied);
    },
    calculate(input: ValidatedBirthInput) {
      if (!applied) throw new Error('Iztro runtime config has not been applied');
      const gender = input.gender === 'male' ? '男' : '女';
      const astrolabe = input.calendar === 'solar'
        ? module.astro.bySolar(input.date, input.timeIndex, gender, applied.fixLeap, applied.language)
        : module.astro.byLunar(input.date, input.timeIndex, gender, input.isLeapMonth, applied.fixLeap, applied.language);
      return snapshotWithPalaceTransformations(module,astrolabe);
    },
    calculateHoroscope(input: ValidatedBirthInput, targetDate: string, targetTimeIndex?: number) {
      if (!applied) throw new Error('Iztro runtime config has not been applied');
      const gender = input.gender === 'male' ? '男' : '女';
      const astrolabe = input.calendar === 'solar'
        ? module.astro.bySolar(input.date, input.timeIndex, gender, applied.fixLeap, applied.language)
        : module.astro.byLunar(input.date, input.timeIndex, gender, input.isLeapMonth, applied.fixLeap, applied.language);
      return { astrolabe: snapshotWithPalaceTransformations(module,astrolabe), horoscope: astrolabe.horoscope(targetDate, targetTimeIndex).toJSON() };
    },
  };
}
